import { useCallback, useMemo, useState } from "react";
import { useAppFeedback } from "../useAppFeedback";
import dayjs, { type Dayjs } from "dayjs";
import type { TFunction } from "i18next";
import type { AuthUser } from "../../context/AuthContext";
import type { DrinkActivitySummary, DrinkOption } from "../../services/drinkService";
import { deleteDrinkActivity, updateDrinkActivity } from "../../services/drinks/DrinkService";
import { DEFAULT_DRINK_DURATION_MINUTES } from "./drinkStats/constants";

type Props = {
    user: AuthUser | null;
    isAuthenticated: boolean;
    availableDrinks: DrinkOption[];
    loadAvailableDrinks: () => Promise<DrinkOption[]>;
    loadActivities: () => Promise<void>;
    t: TFunction;
};

export const useDrinkStatsEditActions = ({ user, isAuthenticated, availableDrinks, loadAvailableDrinks, loadActivities, t }: Props) => {
    const { message } = useAppFeedback();
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingActivity, setEditingActivity] = useState<DrinkActivitySummary | null>(null);
    const [editDrinkId, setEditDrinkId] = useState<number | null>(null);
    const [editAmount, setEditAmount] = useState(0);
    const [editTimestamp, setEditTimestamp] = useState<Dayjs | null>(null);
    const [editDurationMinutes, setEditDurationMinutes] = useState(DEFAULT_DRINK_DURATION_MINUTES);
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    const handleOpenEditModal = useCallback(async (activity: DrinkActivitySummary) => {
        if (!activity.activityId) {
            message.warning(t("drinkStats.messages.cannotEditYet"));
            return;
        }
        let drinks = availableDrinks;
        if (drinks.length === 0 && isAuthenticated) drinks = await loadAvailableDrinks();
        const fallbackDrinkId = activity.drinkId ?? drinks[0]?.id ?? null;
        if (fallbackDrinkId === null) {
            message.warning(t("drinkStats.messages.noDrinksToEdit"));
            return;
        }
        setEditingActivity(activity);
        setEditDrinkId(fallbackDrinkId);
        setEditAmount(activity.amountMl);
        setEditTimestamp(activity.timestamp ? dayjs(activity.timestamp) : dayjs());
        setEditDurationMinutes(activity.durationMinutes != null && activity.durationMinutes >= 0
            ? activity.durationMinutes : DEFAULT_DRINK_DURATION_MINUTES);
        setIsEditModalVisible(true);
    }, [availableDrinks, isAuthenticated, loadAvailableDrinks, t, message]);

    const handleEditModalClose = useCallback(() => {
        setIsEditModalVisible(false);
        setEditingActivity(null);
        setEditDrinkId(null);
        setEditAmount(0);
        setEditTimestamp(null);
        setEditDurationMinutes(DEFAULT_DRINK_DURATION_MINUTES);
    }, []);

    const editStartTimestamp = useMemo(() => editTimestamp?.subtract(editDurationMinutes || 0, "minute") ?? null,
        [editTimestamp, editDurationMinutes]);
    const handleEditStartChange = useCallback((value: Dayjs | null) => {
        if (value && editTimestamp) setEditDurationMinutes(Math.max(0, Math.round(editTimestamp.diff(value, "minute"))));
    }, [editTimestamp]);

    const handleEditSubmit = useCallback(async () => {
        if (!user || editingActivity?.activityId == null) return void message.error(t("drinkStats.messages.editNoEntry"));
        if (editDrinkId === null) return void message.warning(t("drinkStats.messages.editSelectDrink"));
        if (!editTimestamp) return void message.warning(t("drinkStats.messages.editSelectFinish"));
        if (!editAmount || editAmount <= 0) return void message.warning(t("drinkStats.messages.editAmountMin"));
        const drink = availableDrinks.find((item) => item.id === editDrinkId);
        if (!drink) return void message.error(t("drinkStats.messages.editDrinkUnavailable"));
        setIsSavingEdit(true);
        try {
            const consumedDate = editTimestamp.toDate();
            const durationMinutes = Math.max(0, Math.round(editDurationMinutes));
            const startedAt = durationMinutes > 0
                ? new Date(consumedDate.getTime() - durationMinutes * 60 * 1000) : consumedDate;
            await updateDrinkActivity(user, editingActivity.activityId, drink, editAmount, consumedDate, {
                durationMinutes, startedAt, eventId: editingActivity.eventId ?? null,
            });
            message.success(t("drinkStats.messages.editSuccess"));
            handleEditModalClose();
            await loadActivities();
        } catch (error: any) {
            message.error(typeof error?.message === "string" ? error.message : t("drinkStats.messages.editError"));
        } finally {
            setIsSavingEdit(false);
        }
    }, [user, editingActivity, editDrinkId, editTimestamp, editAmount, availableDrinks, loadActivities,
        handleEditModalClose, editDurationMinutes, t, message]);

    const handleDeleteActivity = useCallback(async (activity: DrinkActivitySummary) => {
        if (!user) return void message.error(t("drinkStats.messages.loginAgain"));
        if (!activity.activityId) return void message.warning(t("drinkStats.messages.deleteNotAllowed"));
        try {
            await deleteDrinkActivity(user, activity.activityId);
            message.success(t("drinkStats.messages.deleteSuccess"));
            if (editingActivity?.activityId === activity.activityId) handleEditModalClose();
            await loadActivities();
        } catch (error: any) {
            message.error(typeof error?.message === "string" ? error.message : t("drinkStats.messages.deleteError"));
        }
    }, [user, editingActivity, handleEditModalClose, loadActivities, t, message]);

    return { isEditModalVisible, isSavingEdit, editDrinkId, setEditDrinkId, editAmount, setEditAmount,
        editTimestamp, setEditTimestamp, editDurationMinutes, setEditDurationMinutes,
        editStartTimestamp, handleOpenEditModal, handleEditModalClose, handleEditStartChange,
        handleEditSubmit, handleDeleteActivity };
};
